import { t } from 'i18next';
import { useMutation, useQuery } from 'react-query';

import { FormActions, NotificationPosition } from 'coreOld/enums';
import { useHandleErrors, useInfiniteQuery } from 'coreOld/hooks';
import { actionLoadingMessage, actionMessage, getPlural } from 'coreOld/utils';
import { requestService } from 'support/services';
import { IRequest, IRequestFiltersParams, ISupportRequestListPayload } from 'support/types';

interface IRequestActionOptions {
  notificationPosition?: NotificationPosition;
  multiple?: boolean;
}

export const useGetRequestList = (requestPayload?: ISupportRequestListPayload) =>
  useInfiniteQuery<IRequest>(requestService.getMany, 'requestList', requestPayload);

export const useGetSupportRequest = (criteria: IRequestFiltersParams, enabled = true) => {
  const requestPayload = {
    filters: criteria,
  };

  const { data, isLoading, isFetching, isError, error } = useQuery(
    ['supportRequest', criteria],
    ({ signal }) => requestService.getOne(requestPayload, signal),
    {
      keepPreviousData: false,
      enabled,
    }
  );

  return {
    record: data?.record ?? null,
    isLoading,
    isFetching,
    isError,
    statusCode: (error as any)?.response?.status ?? null,
  };
};

export const useGetRequestTypes = () => {
  const { data, isLoading, isError } = useQuery('requestTypes', ({ signal }) =>
    requestService.getTypes(signal)
  );

  return {
    records: data?.records ?? [],
    isLoading,
    isError,
  };
};

export const useGetRequestStatuses = () => {
  const { data, isLoading, isError } = useQuery(
    'requestStatuses',
    ({ signal }) => requestService.getStatuses(signal),
    {
      keepPreviousData: false,
    }
  );

  return {
    records: data?.records ?? [],
    isLoading,
    isError,
  };
};

export const useRequestActions = (options?: IRequestActionOptions) => ({
  ...useUpsertRequest(options),
  ...useUpsertRequests(options),
});

const useUpsertRequest = (options?: IRequestActionOptions) => {
  const defaultOptions = {
    notificationPosition: NotificationPosition.MODAL,
    ...options,
  };

  const { errors } = useHandleErrors(
    actionMessage(t('labelSupportRequest')),
    actionLoadingMessage(t('labelSupportRequest')),
    defaultOptions.notificationPosition,
    ['requestList', 'jobs'],
    FormActions.UPSERT
  );

  const { mutateAsync } = useMutation((data) => requestService.upsertRequest(data), {
    ...errors,
  });

  const upsertRequest = (data) => mutateAsync(data);

  return { upsertRequest };
};

const useUpsertRequests = (options?: IRequestActionOptions) => {
  const defaultOptions = {
    multiple: false,
    ...options,
  };

  const { errors } = useHandleErrors(
    actionMessage(getPlural(t('labelSupportRequest'), defaultOptions.multiple)),
    actionLoadingMessage(getPlural(t('labelSupportRequest'), defaultOptions.multiple)),
    NotificationPosition.MODAL,
    ['requestList'],
    FormActions.UPSERT
  );

  const { mutateAsync } = useMutation((data) => requestService.upsertRequests(data), {
    ...errors,
  });

  const upsertRequests = (data) => mutateAsync(data);

  return { upsertRequests };
};
